import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { Observable } from 'rxjs';
import { LayoutAspect } from '@degreed/apollo-angular';

import {
  NavigationViewModel,
  valueFromInput,
  i18n,
  TranslateFn,
} from '../data-service';

@Component({
    selector: 'dgx-org-branding-navigation',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'tw-col-span-full tw-flex tw-flex-col tw-gap-y-8 lg:tw-col-start-2 lg:-tw-col-end-2',
    },
    template: `
    <header>
      <h3 class="tw-text-2xl tw-font-bold">
        {{ t('Navigation', i18n.navigation.title) }}
      </h3>
      <p class="tw-text-sm tw-text-neutral-600">
        {{
          t(
            'Customize the appearance of the side navigation and header for your users. All changes can be previewed in the example below.',
            i18n.navigation.description
          )
        }}
      </p>
    </header>

    <div
      class="tw-flex tw-flex-col tw-gap-x-4 tw-gap-y-10 md:tw-flex-row md:tw-justify-between"
    >
      <!-- Logo Mark Loader -->
      <dgx-org-branding-uploader
        [name]="t('Mark Upload', i18n.navigation.mark)"
        [required]="true"
        [hint]="
          t('Appears in the collapsed navigation', i18n.navigation.markHint)
        "
        [sizeHint]="
          t(
            'Recommended dimensions: {{resolutionWidth}} x {{resolutionHeight}} pixels',
            i18n.navigation.markSizeHint,
            { resolutionWidth: '320', resolutionHeight: '320' }
          )
        "
        [state]="navigation.mark"
        [t]="t"
        (change)="navigation.updateMark($event)"
      ></dgx-org-branding-uploader>

      <!-- Logo Loader -->
      <dgx-org-branding-uploader
        [name]="t('Logo Upload', i18n.navigation.logo)"
        [hint]="
          t('Appears in the expanded navigation', i18n.navigation.logoHint)
        "
        [sizeHint]="
          t(
            'Recommended dimensions: {{resolutionWidth}} x {{resolutionHeight}} pixels',
            i18n.navigation.logoSizeHint,
            { resolutionWidth: '1520', resolutionHeight: '320' }
          )
        "
        [state]="navigation.logo"
        [t]="t"
        (change)="navigation.updateLogo($event)"
      ></dgx-org-branding-uploader>
    </div>
    <div>
      <h3 class="tw-text-sm tw-font-semibold">
        {{ t('Colors', i18n.navigation.colors.label) }}
      </h3>
      <div
        class="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-gap-6 md:tw-gap-12"
      >
        <div
          class="tw-flex tw-items-center tw-gap-3 sm:tw-border-r sm:tw-border-neutral-300 sm:tw-pr-6 md:tw-pr-12"
        >
          <label for="background-color" class="tw-text-xs tw-font-extrabold"
            >{{ t('Background', i18n.navigation.colors.backgroundLabel) }}
            <span class="tw-sr-only">
              {{ getValueLabel(navigation.colors.background) }}
            </span></label
          >
          <input
            id="background-color"
            name="background-color"
            [attr.aria-description]="getAriaDescription('Background')"
            type="color"
            class="tw-h-6 tw-w-10 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
            [value]="navigation.colors.background"
            (input)="
              navigation.updateColor(valueFromInput($event), 'background')
            "
          />
        </div>

        <div
          class="tw-flex tw-items-center tw-gap-3 sm:tw-border-r sm:tw-border-neutral-300 sm:tw-pr-6 md:tw-pr-12"
        >
          <label for="highlight-color" class="tw-text-xs tw-font-extrabold">
            {{ t('Highlight', i18n.navigation.colors.highlightLabel) }}
            <span class="tw-sr-only">
              {{ getValueLabel(navigation.colors.highlight) }}
            </span>
          </label>
          <input
            id="highlight-color"
            [attr.aria-description]="getAriaDescription('Highlight')"
            name="highlight-color"
            type="color"
            class="tw-h-6 tw-w-10 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
            [value]="navigation.colors.highlight"
            (input)="
              navigation.updateColor(valueFromInput($event), 'highlight')
            "
          />
        </div>

        <div
          class="tw-flex tw-items-center tw-gap-3 sm:tw-border-r sm:tw-border-neutral-300 sm:tw-pr-6 md:tw-pr-12"
        >
          <label for="text-color" class="tw-text-xs tw-font-extrabold"
            >{{ t('Text', i18n.navigation.colors.textLabel) }}
            <span class="tw-sr-only">
              {{ getValueLabel(navigation.colors.text) }}
            </span>
          </label>
          <input
            id="text-color"
            [attr.aria-description]="getAriaDescription('Text')"
            name="text-color"
            type="color"
            class="tw-h-6 tw-w-10 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
            [value]="navigation.colors.text"
            (input)="navigation.updateColor(valueFromInput($event), 'text')"
          />
        </div>

        <div class="tw-flex tw-items-center tw-gap-3">
          <label for="separator-color" class="tw-text-xs tw-font-extrabold"
            >{{ t('Separator', i18n.navigation.colors.separatorLabel) }}
            <span class="tw-sr-only">
              {{ getValueLabel(navigation.colors.separator) }}
            </span>
          </label>
          <input
            id="separator-color"
            [attr.aria-description]="getAriaDescription('Separator')"
            name="separator-color"
            type="color"
            class="tw-h-6 tw-w-10 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
            [value]="navigation.colors.separator"
            (input)="
              navigation.updateColor(valueFromInput($event), 'separator')
            "
          />
        </div>
      </div>
      <!-- @TODO: Replace with Apollo alert component when available -->
      <div
        class="tw-col-span-full tw-mt-4 tw-rounded-lg tw-bg-yellow-100 tw-text-yellow-800 xl:tw-col-span-6 xl:tw-col-start-3"
        role="alert"
      >
        @if (
          !navigation.colors.a11y.text || !navigation.colors.a11y.highlight
        ) {
          <div
            class="tw-space-y-4 tw-rounded-l-lg tw-border-l-8 tw-border-yellow-600 tw-px-3 tw-py-4"
          >
            <div class="tw-overflow-hidden">
              <h4 class="tw-flex tw-items-center tw-gap-2 tw-font-semibold">
                <da-icon
                  icon="exclamation-triangle"
                  type="solid"
                  className="tw-inline-block tw-h-4 tw-w-4 tw-text-yellow-600"
                />
                <span>
                  {{
                    t(
                      'The combination of colors does not meet WCAG 4.5:1 contrast ratio requirements.',
                      i18n.navigation.colors.a11y.warning
                    )
                  }}</span
                >
              </h4>
              <div
                class="tw-mt-2 tw-flex tw-max-h-96 tw-flex-col tw-flex-wrap tw-gap-2 tw-overflow-auto tw-text-sm"
              >
                <div>
                  @if (!navigation.colors.a11y.highlight) {
                    <p>
                      {{
                        t(
                          'The contrast ratio between the selected background color and highlight color is too low.',
                          i18n.navigation.colors.a11y.highlight
                        )
                      }}
                    </p>
                  }
                  @if (!navigation.colors.a11y.text) {
                    <p>
                      {{
                        t(
                          'The contrast ratio between the selected background color and text color is too low.',
                          i18n.navigation.colors.a11y.text
                        )
                      }}
                    </p>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
    <dgx-org-branding-preview
      [layout$]="layout$"
      [t]="t"
    ></dgx-org-branding-preview>
  `,
    standalone: false
})
export class OrgBrandingNavigationComponent {
  @Input() public navigation: NavigationViewModel;
  @Input() public layout$: Observable<LayoutAspect>;
  @Input() public t: TranslateFn = (val: string) => val;
  public valueFromInput = valueFromInput;

  public i18n = i18n; // template access to i18n keys for OrgBranding

  public getValueLabel(color: string): string {
    return this.t(`Value:${color}.`, 'OrgSettingsNav_BrandingColorWellValue', {
      color,
    });
  }
  public getAriaDescription(colorType: string): string {
    const label = this.t(colorType, colorType);
    return this.t(
      ` Edit ${label} color in navigation bar.`,
      'OrgSettingsNav_BrandingColorWellDesc',
      {
        label,
      }
    );
  }
}
